import React from "react";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseDatePicker from "../../BaseComponents/BaseDatePicker";
import BaseSelect from "../../BaseComponents/BaseSelect";
import BaseRadioGroup from "../../BaseComponents/BaseRadio";
import BaseCheckbox from "../../BaseComponents/BaseCheckbox";
import {
  formFields,
  GENDER_OP,
  hrEnums,
  PHYSICALLY_CHALLENGED_OP,
} from "../../Constant/HR_Staff/hrEnums";
import { PlaceHolderFormat } from "../../Constant/requireMessage";
import { digitRegex, SelectPlaceHolder } from "../../Constant/validation";
import { extractDate, RequiredSign } from "../../Constant/common";
import { ProfileSection, InfoField, SectionDivider } from "../../BaseComponents/ProfileSection";
import { Heart } from "react-feather";

const PersonalDetails = ({
  staffForm,
  viewPersonalMode,
  handlePersonalEdit,
  handlePersonalDiscard,
  handlePhysicalChange,
  editPersonalMode,
  selectedPhysical,
  selectedGender,
  handleGenderChange,
  fetchDistrict,
  district,
  states,
  staffData,
  handleMaritalStatus,
  btnLoader,
  handleCheckboxChange,
  isChecked,
}) => {
  const { values, initialValues } = staffForm;
  const isSaveDisabled = React.useMemo(() => {
    const fields = [
      "gender",
      "dob",
      "blood_group",
      "emergency_contact",
      "physically_challenged",
      "father_name",
      "mother_name",
      "marital_status",
      "spouse_name",
      "address_line1",
      "address_line2",
      "state",
      "district",
      "postal_code",
      "permanent_address_line1",
      "permanent_address_line2",
      "permanent_state",
      "permanent_district",
      "permanent_postal_code",
    ];

    const normalizeDate = (d) => {
      if (!d) return "";
      if (d instanceof Date) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }
      const match = String(d).match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})/);
      if (match) {
        return `${match[1]}-${match[2]}-${match[3]}`;
      }
      const date = new Date(d);
      return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
    };

    return !fields.some((field) => {
      const val = values[field];
      const initVal = initialValues[field];

      if (field === "dob") {
        return normalizeDate(val) !== normalizeDate(initVal);
      }

      const normalizedVal = val === null || val === undefined ? "" : String(val).trim();
      const normalizedInitVal =
        initVal === null || initVal === undefined ? "" : String(initVal).trim();
      return normalizedVal !== normalizedInitVal;
    });
  }, [values, initialValues]);

  return (
    <ProfileSection
      id="personal-info"
      title={hrEnums.PERSONAL_INFO}
      icon={Heart}
      iconColor="#f43f5e"
      viewMode={viewPersonalMode}
      editMode={editPersonalMode}
      onEdit={handlePersonalEdit}
      onDiscard={handlePersonalDiscard}
      isEditable={true}
      btnLoader={btnLoader}
      animDelay="60ms"
      isSaveDisabled={isSaveDisabled}
    >
      {viewPersonalMode ? (
        <>
          <div className="row">
            <div className="col-6 col-md-3">
              <InfoField
                label={hrEnums.GENDER}
                value={staffData?.staffGeneralInformation?.gender}
                required
              />
            </div>
            <div className="col-6 col-md-3">
              <InfoField
                label={hrEnums.DOB}
                required
                value={
                  staffData?.staffGeneralInformation?.dob
                    ? extractDate(staffData.staffGeneralInformation.dob)
                    : null
                }
              />
            </div>
            <div className="col-6 col-md-3">
              <InfoField
                label={hrEnums.BLOOD_GRP}
                value={staffData?.staffGeneralInformation?.blood_group}
              />
            </div>
            <div className="col-6 col-md-3">
              <InfoField
                label={hrEnums.EMERGENCY_CONTACT}
                value={staffData?.staffGeneralInformation?.emergency_contact}
              />
            </div>
            <div className="col-6 col-md-3">
              <InfoField
                label={hrEnums.FATHER_NAME}
                value={staffData?.staffGeneralInformation?.father_name}
              />
            </div>
            <div className="col-6 col-md-3">
              <InfoField
                label={hrEnums.MOTHER_NAME}
                value={staffData?.staffGeneralInformation?.mother_name}
              />
            </div>
            <div className="col-6 col-md-3">
              <InfoField
                label={hrEnums.MARITAL_STATUS}
                value={staffData?.staffGeneralInformation?.marital_status}
              />
            </div>
            {staffData?.staffGeneralInformation?.marital_status === hrEnums.MARRIED && (
              <div className="col-6 col-md-3">
                <InfoField
                  label={hrEnums.SPOUSE_NAME}
                  value={staffData?.staffGeneralInformation?.spouse_name}
                />
              </div>
            )}
            <div className="col-6 col-md-3">
              <InfoField
                label={hrEnums.PHYSICALLY_CHALLENGED}
                value={staffData?.staffGeneralInformation?.physically_challenged}
              />
            </div>
          </div>

          {staffData?.staffAddress?.map((address, i) => (
            <div key={i}>
              <SectionDivider label={address.address_type} />
              <div className="row">
                <div className="col-6 col-md-3">
                  <InfoField label={hrEnums.ADDRESS_LINE1} value={address.address_line_1} />
                </div>
                <div className="col-6 col-md-3">
                  <InfoField label={hrEnums.ADDRESS_LINE2} value={address.address_line_2} />
                </div>
                <div className="col-6 col-md-3">
                  <InfoField label={hrEnums.DISTRICT} value={address.district?.district_name} />
                </div>
                <div className="col-6 col-md-3">
                  <InfoField label={hrEnums.STATE} value={address.state?.state_name} />
                </div>
                <div className="col-6 col-md-3">
                  <InfoField label={hrEnums.POSTAL_CODE} value={address.postal_code} mono />
                </div>
              </div>
            </div>
          ))}
        </>
      ) : (
        <>
          <div className="row g-3">
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <label className="form-label">
                {hrEnums.GENDER}
                <span className="text-danger ms-1">{RequiredSign}</span>
              </label>
              <BaseRadioGroup
                name={formFields.gender}
                options={GENDER_OP}
                selectedValue={selectedGender}
                onChange={handleGenderChange}
                value={staffForm.values.gender}
                touched={staffForm.touched.gender}
                error={staffForm.errors.gender}
                handleBlur={staffForm.handleBlur}
                disabled={btnLoader}
                inline={true}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseDatePicker
                name={formFields.dob}
                label={
                  <>
                    {hrEnums.DOB}
                    <span className="text-danger ms-1">{RequiredSign}</span>
                  </>
                }
                placeholder={PlaceHolderFormat(hrEnums.DOB)}
                value={staffForm.values.dob}
                touched={staffForm.touched.dob}
                error={staffForm.errors.dob}
                onBlur={staffForm.handleBlur}
                onChange={(v) => staffForm.setFieldValue(formFields.dob, v)}
                maxDate={new window.Date()}
                disabled={btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseSelect
                id={formFields.blood_group}
                name={formFields.blood_group}
                label={hrEnums.BLOOD_GRP}
                options={hrEnums.BLOOD_GRP_OP}
                placeholder={SelectPlaceHolder(hrEnums.BLOOD_GRP)}
                handleChange={(f, v) => staffForm.setFieldValue(f, v)}
                handleBlur={() => staffForm.setFieldTouched(hrEnums.BLOOD_GRP, true)}
                value={staffForm.values.blood_group}
                touched={staffForm.touched.blood_group}
                error={staffForm.errors.blood_group}
                isDisabled={btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseInput
                name={formFields.emergency_contact}
                type="text"
                label={hrEnums.EMERGENCY_CONTACT}
                placeholder={PlaceHolderFormat(hrEnums.EMERGENCY_CONTACT)}
                value={staffForm.values.emergency_contact}
                touched={staffForm.touched.emergency_contact}
                error={staffForm.errors.emergency_contact}
                handleBlur={staffForm.handleBlur}
                maxLength={10}
                handleChange={(e) => {
                  if (digitRegex.test(e?.target?.value)) staffForm.handleChange(e);
                }}
                disabled={btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <label className="form-label">{hrEnums.PHYSICALLY_CHALLENGED}</label>
              <BaseRadioGroup
                name={formFields.physically_challenged}
                options={PHYSICALLY_CHALLENGED_OP}
                selectedValue={selectedPhysical}
                onChange={handlePhysicalChange}
                value={staffForm.values.physically_challenged}
                touched={staffForm.touched.physically_challenged}
                error={staffForm.errors.physically_challenged}
                handleBlur={staffForm.handleBlur}
                disabled={btnLoader}
                inline={true}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseInput
                name={formFields.father_name}
                type="text"
                label={hrEnums.FATHER_NAME}
                placeholder={PlaceHolderFormat(hrEnums.FATHER_NAME)}
                value={staffForm.values.father_name}
                touched={staffForm.touched.father_name}
                error={staffForm.errors.father_name}
                handleBlur={staffForm.handleBlur}
                handleChange={staffForm.handleChange}
                disabled={btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseInput
                name={formFields.mother_name}
                type="text"
                label={hrEnums.MOTHER_NAME}
                placeholder={PlaceHolderFormat(hrEnums.MOTHER_NAME)}
                value={staffForm.values.mother_name}
                touched={staffForm.touched.mother_name}
                error={staffForm.errors.mother_name}
                handleBlur={staffForm.handleBlur}
                handleChange={staffForm.handleChange}
                disabled={btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseSelect
                id={formFields.marital_status}
                name={formFields.marital_status}
                label={hrEnums.MARITAL_STATUS}
                options={hrEnums.MARITAL_STATUS_OP}
                placeholder={SelectPlaceHolder(hrEnums.MARITAL_STATUS)}
                handleChange={(f, v) => handleMaritalStatus(f, v)}
                handleBlur={() => staffForm.setFieldTouched(hrEnums.MARITAL_STATUS, true)}
                value={staffForm.values.marital_status}
                touched={staffForm.touched.marital_status}
                error={staffForm.errors.marital_status}
                isDisabled={btnLoader}
              />
            </div>
            {staffForm.values.marital_status === hrEnums.MARRIED && (
              <div className="col-xl-3 col-lg-3 col-md-6 col-12">
                <BaseInput
                  name={formFields.spouse_name}
                  type="text"
                  label={hrEnums.SPOUSE_NAME}
                  placeholder={PlaceHolderFormat(hrEnums.SPOUSE_NAME)}
                  value={staffForm.values.spouse_name}
                  touched={staffForm.touched.spouse_name}
                  error={staffForm.errors.spouse_name}
                  handleBlur={staffForm.handleBlur}
                  handleChange={staffForm.handleChange}
                  disabled={btnLoader}
                />
              </div>
            )}
          </div>

          <SectionDivider label={hrEnums.CURRENT_ADDRESS} />
          <div className="row g-3">
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseInput
                label={hrEnums.ADDRESS_LINE1}
                name={formFields.address_line1}
                type="text"
                placeholder={PlaceHolderFormat(hrEnums.ADDRESS_LINE1)}
                value={staffForm.values.address_line1}
                touched={staffForm.touched.address_line1}
                error={staffForm.errors.address_line1}
                handleBlur={staffForm.handleBlur}
                handleChange={staffForm.handleChange}
                disabled={btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseInput
                label={hrEnums.ADDRESS_LINE2}
                name={formFields.address_line2}
                type="text"
                placeholder={PlaceHolderFormat(hrEnums.ADDRESS_LINE2)}
                value={staffForm.values.address_line2}
                touched={staffForm.touched.address_line2}
                error={staffForm.errors.address_line2}
                handleBlur={staffForm.handleBlur}
                handleChange={staffForm.handleChange}
                disabled={btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseSelect
                id={formFields.state}
                name={formFields.state}
                label={hrEnums.STATE}
                options={states}
                placeholder={SelectPlaceHolder(hrEnums.STATE)}
                handleChange={(f, v) => {
                  staffForm.setFieldValue(f, v);
                  fetchDistrict(v);
                }}
                handleBlur={() => staffForm.setFieldTouched(hrEnums.STATE, true)}
                value={staffForm.values.state}
                touched={staffForm.touched.state}
                error={staffForm.errors.state}
                isDisabled={btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseSelect
                id={formFields.district}
                name={formFields.district}
                label={hrEnums.DISTRICT}
                options={district}
                placeholder={SelectPlaceHolder(hrEnums.DISTRICT)}
                handleChange={(f, v) => staffForm.setFieldValue(f, v)}
                handleBlur={() => staffForm.setFieldTouched(hrEnums.DISTRICT, true)}
                value={staffForm.values.district}
                touched={staffForm.touched.district}
                error={staffForm.errors.district}
                isDisabled={district === null || btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseInput
                label={hrEnums.POSTAL_CODE}
                name={formFields.postal_code}
                type="number"
                placeholder={PlaceHolderFormat(hrEnums.POSTAL_CODE)}
                value={staffForm.values.postal_code}
                touched={staffForm.touched.postal_code}
                error={staffForm.errors.postal_code}
                handleBlur={staffForm.handleBlur}
                handleChange={(e) => {
                  if (e?.target?.value?.length <= 6 && digitRegex.test(e?.target?.value))
                    staffForm.handleChange(e);
                }}
                disabled={btnLoader}
              />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <BaseCheckbox
              id="sameAsCurrentAddress"
              checked={isChecked}
              onChange={(e) => handleCheckboxChange(e)}
              label={hrEnums.CURRENT_CHECKBOX}
              disabled={btnLoader}
            />
          </div>

          <SectionDivider label={hrEnums.PERMANENT_ADDRESS} />
          <div className="row g-3">
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseInput
                label={hrEnums.ADDRESS_LINE1}
                name={formFields.permanent_address_line1}
                type="text"
                placeholder={PlaceHolderFormat(hrEnums.ADDRESS_LINE1)}
                value={staffForm.values.permanent_address_line1}
                touched={staffForm.touched.permanent_address_line1}
                error={staffForm.errors.permanent_address_line1}
                handleBlur={staffForm.handleBlur}
                handleChange={staffForm.handleChange}
                disabled={isChecked || btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseInput
                label={hrEnums.ADDRESS_LINE2}
                name={formFields.permanent_address_line2}
                type="text"
                placeholder={PlaceHolderFormat(hrEnums.ADDRESS_LINE2)}
                value={staffForm.values.permanent_address_line2}
                touched={staffForm.touched.permanent_address_line2}
                error={staffForm.errors.permanent_address_line2}
                handleBlur={staffForm.handleBlur}
                handleChange={staffForm.handleChange}
                disabled={isChecked || btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseSelect
                id={formFields.permanent_state}
                name={formFields.permanent_state}
                label={hrEnums.STATE}
                options={states}
                placeholder={SelectPlaceHolder(hrEnums.STATE)}
                handleChange={(f, v) => {
                  staffForm.setFieldValue(f, v);
                  fetchDistrict(v);
                }}
                handleBlur={() => staffForm.setFieldTouched(hrEnums.STATE, true)}
                value={staffForm.values.permanent_state}
                touched={staffForm.touched.permanent_state}
                error={staffForm.errors.permanent_state}
                isDisabled={isChecked || btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseSelect
                id={formFields.permanent_district}
                name={formFields.permanent_district}
                label={hrEnums.DISTRICT}
                options={district}
                placeholder={SelectPlaceHolder(hrEnums.DISTRICT)}
                handleChange={(f, v) => staffForm.setFieldValue(f, v)}
                handleBlur={() => staffForm.setFieldTouched(hrEnums.DISTRICT, true)}
                value={staffForm.values.permanent_district}
                touched={staffForm.touched.permanent_district}
                error={staffForm.errors.permanent_district}
                isDisabled={district === null || isChecked || btnLoader}
              />
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-12">
              <BaseInput
                label={hrEnums.POSTAL_CODE}
                name={formFields.permanent_postal_code}
                type="number"
                placeholder={PlaceHolderFormat(hrEnums.POSTAL_CODE)}
                value={staffForm.values.permanent_postal_code}
                touched={staffForm.touched.permanent_postal_code}
                error={staffForm.errors.permanent_postal_code}
                handleBlur={staffForm.handleBlur}
                disabled={isChecked || btnLoader}
                handleChange={(e) => {
                  if (e?.target?.value?.length <= 6 && digitRegex.test(e?.target?.value))
                    staffForm.handleChange(e);
                }}
              />
            </div>
          </div>
        </>
      )}
    </ProfileSection>
  );
};

export default PersonalDetails;
